import { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Download, Upload, CheckCircle2, AlertTriangle, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  {
    key: "employees",
    title: "Employees",
    eyebrow: "1. People records",
    description: "Upsert by employee_id. Existing rows are updated, new rows are created.",
    columns: ["employee_id", "name", "email", "department", "roles (pipe '|' separated)", "system_role", "joining_date", "initial_password"],
    endpoint: "/admin/import/employees",
    templateEndpoint: "/admin/import/template/employees",
  },
  {
    key: "competency-map",
    title: "Competency Map",
    eyebrow: "2. Role → skill required level",
    description: "Grouped by role. skill_name must match an existing skill — import Skills first.",
    columns: ["role", "skill_name", "required_level (2, 3 or 4)"],
    endpoint: "/admin/import/competency-map",
    templateEndpoint: "/admin/import/template/competency-map",
  },
  {
    key: "skills",
    title: "Skills",
    eyebrow: "3. Skills master",
    description: "Upsert by code.",
    columns: ["code (e.g. sk01)", "name (e.g. 5S)"],
    endpoint: "/admin/import/skills",
    templateEndpoint: "/admin/import/template/skills",
  },
  {
    key: "questions",
    title: "Question Bank",
    eyebrow: "4. MCQ questions",
    description: "Upsert by question_id. skill_code must reference an existing skill.",
    columns: ["question_id", "skill_code", "difficulty (basic/advance)", "question_number", "question_text", "option_1", "option_2", "option_3", "option_4", "correct_option (A/B/C/D)"],
    endpoint: "/admin/import/questions",
    templateEndpoint: "/admin/import/template/questions",
  },
  {
    key: "historical-assessments",
    title: "Historical Assessments",
    eyebrow: "5. Read-only history",
    description: "Imported as status=completed. Does NOT trigger gap or training need creation.",
    columns: ["employee_id", "skill_code", "skill_name", "type (TNI/Effectiveness)", "completed_date (YYYY-MM-DD)", "score_percent", "achieved_level (L1-L4)", "required_level (L2/L3/L4)", "result (pass/fail)"],
    endpoint: "/admin/import/historical-assessments",
    templateEndpoint: "/admin/import/template/historical-assessments",
  },
];

export default function DataImport() {
  return (
    <div data-testid="data-import-page">
      <PageHeader
        eyebrow="Super Admin · Bulk import"
        title="Data Import"
        subtitle="Migrate v1 data or bulk-load new records. All imports are additive — never destructive. Download a template to see the expected format."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "Data Import" }]}
      />
      <div className="px-4 md:px-10 py-8 space-y-6">
        {SECTIONS.map((s) => <ImportSection key={s.key} section={s} />)}
      </div>
    </div>
  );
}

function ImportSection({ section }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleFile = (e) => {
    setFile(e.target.files[0] || null);
    setResult(null);
    setError("");
  };

  const resetUploadArea = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const downloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await fetch(`${API}${section.templateEndpoint}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `template_${section.key}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully");
    } catch {
      toast.error("Export failed — please try again");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setError(""); setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(section.endpoint, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000, // 5 minutes for large imports
      });
      setResult(res.data);
      // Reset the upload area so the section is ready for another file.
      // The result summary stays visible below.
      resetUploadArea();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bc-card" data-testid={`import-section-${section.key}`}>
      <div className="px-5 md:px-6 py-4 border-b border-[#E5E5E5] flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="bc-label">{section.eyebrow}</div>
          <h3 className="font-heading text-lg font-semibold mt-0.5">{section.title}</h3>
          <p className="text-xs text-[#666666] mt-1 max-w-2xl">{section.description}</p>
        </div>
        <button
          type="button"
          onClick={downloadTemplate}
          disabled={downloadingTemplate}
          data-testid={`template-${section.key}`}
          className="self-start inline-flex items-center gap-1.5 text-xs font-medium border border-[#E5E5E5] hover:border-[#0D0D0D] rounded-sm px-3 py-2 transition-colors disabled:opacity-50"
        >
          {downloadingTemplate ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
          {downloadingTemplate ? "Generating…" : "Download template"}
        </button>
      </div>

      <div className="px-5 md:px-6 py-4 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <div className="bc-label mb-2">CSV columns (in order)</div>
          <div className="flex flex-wrap gap-1.5">
            {section.columns.map((c) => (
              <code key={c} className="text-[11px] font-mono bg-[#F9F9F9] border border-[#E5E5E5] rounded-sm px-1.5 py-0.5">
                {c}
              </code>
            ))}
          </div>
        </div>
        <div className="md:col-span-1">
          <div className="bc-label mb-2">Upload</div>
          <label className="dropzone rounded-sm px-4 py-5 flex flex-col items-center justify-center gap-1.5 text-center cursor-pointer" data-testid={`file-zone-${section.key}`}>
            <FileText size={18} className="text-[#666666]" />
            <span className="text-xs font-medium break-all">{file ? file.name : "Click to select a CSV"}</span>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} data-testid={`file-input-${section.key}`} />
          </label>
          <button
            type="button"
            disabled={!file || busy}
            onClick={upload}
            data-testid={`upload-${section.key}`}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 bg-[#C41E1E] hover:bg-[#A31818] text-white text-sm rounded-sm py-2.5 font-medium disabled:opacity-40"
          >
            <Upload size={13} /> {busy ? "Importing…" : "Import"}
          </button>
        </div>
      </div>

      {(result || error) && (
        <div className="border-t border-[#E5E5E5] px-5 md:px-6 py-4" data-testid={`result-${section.key}`}>
          {error && (
            <div className="text-sm text-[#C41E1E] bg-red-50 border border-red-200 rounded-sm px-3 py-2 flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div><strong>Import failed.</strong> {error}</div>
            </div>
          )}
          {result && (
            <ResultSummary result={result} />
          )}
        </div>
      )}
    </div>
  );
}

function ResultSummary({ result }) {
  const ok = result.errors.length === 0;
  return (
    <div>
      <div className={`flex items-center gap-2 text-sm font-medium ${ok ? "text-green-700" : "text-amber-700"}`}>
        {ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
        <span data-testid="result-summary">
          {result.imported} record{result.imported === 1 ? "" : "s"} imported successfully, {result.errors.length} error{result.errors.length === 1 ? "" : "s"}.
        </span>
      </div>
      {result.errors.length > 0 && (
        <div className="mt-3 max-h-60 overflow-y-auto border border-[#E5E5E5] rounded-sm">
          <table className="w-full text-xs">
            <thead className="bg-[#F9F9F9] border-b border-[#E5E5E5]">
              <tr>
                <th className="text-left px-3 py-1.5 bc-label">Row</th>
                <th className="text-left px-3 py-1.5 bc-label">Reason</th>
              </tr>
            </thead>
            <tbody>
              {result.errors.map((e) => (
                <tr key={`${e.row}-${e.reason}`} className="border-b border-[#E5E5E5]">
                  <td className="px-3 py-1.5 font-mono text-[#C41E1E]">{e.row}</td>
                  <td className="px-3 py-1.5">{e.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
